import { ReactNode } from 'react';
import { Iconify } from '@/components/common/iconify';
import { CHANNEL_ICONS } from '@/lib/channel-icons';

const icon = (name: string): ReactNode => <Iconify icon={name} width={20} />;

const appUrl = process.env.NEXT_PUBLIC_SELF_URL;

export const ICONS: { [key: string]: ReactNode } = {
  dashboard: icon(CHANNEL_ICONS.overview),
  file: icon(CHANNEL_ICONS.voiceAgents),
  aifill: icon('si:ai-fill'),
  user: icon('octicon:people-24'),
  flow: icon(CHANNEL_ICONS.voiceWorkflows),
  edit: icon('hugeicons:ai-image'),
  email: icon(CHANNEL_ICONS.directMail),
  emailBulk: icon(CHANNEL_ICONS.email),
  screen: icon(CHANNEL_ICONS.digitalAds),
  map: icon(CHANNEL_ICONS.eddm),
  tour: icon('carbon:flow-logs-vpc'),
  predictions: icon('hugeicons:ai-user'),
  automation: icon('carbon:flow-modeler'),
  informedDelivery: icon(CHANNEL_ICONS.informedDelivery),
  qrCode: icon(CHANNEL_ICONS.qrCode),
  rcs: icon(CHANNEL_ICONS.rcs),
  webRetargeting: icon(CHANNEL_ICONS.webRetargeting),
  webPersonalization: icon(CHANNEL_ICONS.webPersonalization),
  campaignMarkup: icon('solar:tag-price-linear'),
  blynCopilot: icon('humbleicons:ai'),
  analyzer: icon('solar:chart-square-linear'),
  generator: icon('solar:magic-stick-3-linear'),
  templates: icon(CHANNEL_ICONS.voiceCampaigns),
  voice: icon(CHANNEL_ICONS.voice),
  callAnalytics: icon('solar:call-chat-linear'),
  arc: icon('solar:globus-linear'),
  budget: icon('solar:wad-of-money-linear'),
  analytics: icon(CHANNEL_ICONS.analytics),
  attribution: icon(CHANNEL_ICONS.attribution),
  logic: icon('solar:settings-minimalistic-linear'),
};

// ----------------------------------------------------------------------
// Permission required to show each nav item (RBAC). Omit `permission` for items visible to everyone.
// ----------------------------------------------------------------------

export const navData = [
  /**
   * Command Center (Phase 1 — brandbook-styled overview, own scoped shell)
   */
  {
    subheader: {
      title: 'COMMAND CENTER',
      family: true,
      familyColor: '#1B8187',
    },
    items: [
      {
        title: 'Overview',
        path: '/command-center',
        icon: icon('solar:widget-5-linear'),
        info: 'Beta',
        permission: 'command_center.view',
      },
    ],
  },
  /**
   * BLYN (AI Copilot)
   */
  {
    subheader: {
      title: 'BLYN',
      family: true,
      familyColor: '#58C3BE',
    },
    items: [
      { title: 'Agent', path: '/ai-agent', icon: ICONS.blynCopilot, info: 'Beta', permission: 'blyn.view' },
    ],
  },
  /**
   * IRIS (Predict & Enrich + Personalize & Build)
   */
  {
    subheader: {
      title: 'IRIS',
      family: true,
      familyColor: '#58C3BE',
      groups: [
        {
          label: 'Predict & Enrich',
          itemTitles: ['Predictions', 'Audience Builder', 'Data Visualizer'],
        },
        {
          label: 'Personalize & Build',
          itemTitles: ['Logic / Rules', 'Creatives'],
        },
      ],
    },
    items: [
      {
        title: 'Predictions',
        path: '/predictions',
        icon: ICONS.predictions,
        permission: 'audience.view',
      },
      {
        title: 'Audience Builder',
        path: '#',
        icon: ICONS.user,
        permission: 'audience.view',
        children: [
          { title: 'Audience overview', path: '/cdm', permission: 'audience.view' },
          { title: 'Buy a list', path: '/cdm/build-audience', permission: 'audience.create' },
          { title: 'Upload List', path: '/cdm/upload-audience', permission: 'audience.import' },
          { title: 'Saved Audiences', path: '/cdm/saved-audiences', permission: 'audience.view' },
          { title: 'Places of Interest', path: '/cdm/places-of-interest', permission: 'audience.view' },
          { title: 'Saved Polygons', path: '/cdm/saved-polygons', permission: 'audience.view' },
        ],
      },
      {
        title: 'Data Visualizer',
        path: '/visualizer',
        icon: icon('hugeicons:chart-line-data-02'),
        permission: 'report.view',
      },
      {
        title: 'Logic / Rules',
        path: '/logic',
        icon: ICONS.logic,
        permission: 'journey.view',
      },
      {
        title: 'Creatives',
        path: '#',
        icon: ICONS.edit,
        permission: 'asset.view',
        children: [
          { title: 'Creative library', path: '/creatives', permission: 'asset.view' },
          { title: 'Upload Creative', path: '/creatives/upload', permission: 'asset.create' },
          { title: 'Build Creatives', path: '/creatives/build', permission: 'asset.create' },
        ],
      },
    ],
  },
  /**
   * HALO (Activate & Orchestrate)
   */
  {
    subheader: {
      title: 'HALO',
      family: true,
      familyColor: '#1B8187',
      groups: [
        {
          label: 'Activate & Orchestrate',
          itemTitles: ['Channels', 'Journeys', 'Automation Workflows'],
        },
      ],
    },
    items: [
      {
        title: 'Channels',
        path: '#',
        icon: icon('solar:clapperboard-linear'),
        permission: 'campaign.view',
        disableRootRedirect: true,
        children: [
          { title: 'Direct Mail', path: '/campaigns', icon: ICONS.email },
          { title: 'Informed Delivery', path: '/campaigns/informed-delivery', icon: ICONS.informedDelivery },
          {
            title: 'Email',
            path: '/email',
            icon: ICONS.emailBulk,
            children: [
              { title: 'Email Templates', path: '/email/templates' },
              { title: 'Email Accounts', path: '/email/identities' },
              { title: 'Unibox', path: '/email/inbox' },
            ],
          },
          { title: 'Digital Ads', path: '/ads', icon: ICONS.screen },
          { title: 'EDDM', path: '/eddm-campaigns', icon: ICONS.map },
          { title: 'QR Code', path: '/qr-code', icon: ICONS.qrCode },
          // Command Center-only channels — no legacy dashboard exists yet, so these
          // pages render the fixture-backed channel view directly (see
          // src/app/rcs/page.tsx and its siblings) and stay beta-gated.
          { title: 'RCS', path: '/rcs', icon: ICONS.rcs, info: 'Beta', permission: 'command_center.view' },
          { title: 'Web Retargeting', path: '/web-retargeting', icon: ICONS.webRetargeting, info: 'Beta', permission: 'command_center.view' },
          { title: 'Web Personalization', path: '/web-personalization', icon: ICONS.webPersonalization, info: 'Beta', permission: 'command_center.view' },
        ],
      },
      {
        title: 'Journeys',
        path: '/journeys',
        icon: ICONS.tour,
        permission: 'journey.view',
      },
      {
        title: 'Automation Workflows',
        path: '/automation-flows',
        icon: ICONS.automation,
        permission: 'journey.view',
      },
      /**
       * Boostt-INTERNAL-STAFF-ONLY. Not a customer-facing channel — deliberately NOT
       * nested under "Channels" above. Gated by `mailhouse.manage`, a permission real
       * Boostt customers are never granted, so this is invisible to every customer
       * account/sub-account; only Boostt staff roles get it. See
       * `filterNavByPermission.ts` (hides items the user lacks permission for) and
       * `routePathPermissions.ts` (auto-derives the /mailhouse route guard from
       * this entry's + its children's `path` + `permission`, covering
       * /mailhouse/* sub-pages too).
       *
       * Now has 3 real sub-pages (Overview, Partners, XMPie Configuration), so —
       * matching how "Channels"/"Voice" structure their multi-page sections above —
       * this became a `path: '#'` parent with `children` instead of a single flat
       * link. Each child repeats `permission: 'mailhouse.manage'` explicitly (rather
       * than relying on the parent's) so `routePathPermissions.ts`'s
       * `flattenNavDataRules()` — which only reads `permission` off items whose own
       * `path` starts with `/` — still derives a route-guard rule for `/mailhouse`,
       * `/mailhouse/partners`, and `/mailhouse/xmpie` without needing a matching
       * edit to that file's `EXTRA_ROUTE_RULES`.
       */
      {
        title: 'Mailhouse Management',
        path: '#',
        icon: icon('solar:box-minimalistic-linear'),
        permission: 'mailhouse.manage',
        disableRootRedirect: true,
        children: [
          { title: 'Overview', path: '/mailhouse', permission: 'mailhouse.manage' },
          { title: 'Partners', path: '/mailhouse/partners', permission: 'mailhouse.manage' },
          { title: 'XMPie Configuration', path: '/mailhouse/xmpie', permission: 'mailhouse.manage' },
        ],
      },
    ],
  },
  /**
   * SAGE (Voice)
   */
  {
    subheader: {
      title: 'SAGE',
      family: true,
      familyColor: '#ECBF2B',
    },
    items: [
      {
        title: 'Voice',
        path: '#',
        icon: ICONS.voice,
        info: 'Beta',
        disableRootRedirect: true,
        children: [
          { title: 'Voice Analytics', path: '/voice-analytics', icon: ICONS.callAnalytics, permission: 'voice.analytics.view' },
          { title: 'Voice Agents', path: '/voice-agents', icon: ICONS.voice, permission: 'voice.agent.view' },
          {
            title: 'Voice Campaigns',
            path: '/voice-campaigns',
            icon: icon('solar:clapperboard-edit-linear'),
            permission: 'voice.campaign.view',
          },
          {
            title: 'Outbound Scheduler',
            path: '/voice-outbound',
            icon: icon('solar:phone-calling-linear'),
            permission: 'voice.campaign.view',
          },
          {
            title: 'Supervisor',
            path: '/voice-supervisor',
            icon: icon('solar:user-speak-linear'),
            permission: 'voice.supervisor.view',
          },
          {
            title: 'Contacts',
            path: '/voice-contacts',
            icon: icon('solar:users-group-two-rounded-linear'),
            permission: 'voice.contact.view',
          },
          {
            title: 'Saved Segments',
            path: '/voice-segments',
            icon: icon('solar:folder-with-files-linear'),
            permission: 'voice.segment.view',
          },
          {
            title: 'Missed Call Recovery',
            path: '/voice-recovery',
            icon: icon('material-symbols:phone-missed-rounded'),
            permission: 'voice.recovery.view',
          },
          {
            title: 'Callbacks',
            path: '/voice-callbacks',
            icon: icon('solar:phone-calling-rounded-linear'),
            permission: 'voice.callback.view',
          },
          {
            title: 'Session Intents',
            path: '/voice-intents',
            icon: icon('solar:list-check-linear'),
            permission: 'campaign.view',
          },
          {
            title: 'Webhook Subscriptions',
            path: '/voice-webhooks',
            icon: icon('solar:link-round-angle-linear'),
            permission: 'voice.webhook.view',
          },
          {
            title: 'Voicemails',
            path: '/voice-voicemails',
            icon: icon('solar:soundwave-linear'),
            permission: 'voice.voicemail.view',
          },
          {
            title: 'Voice Workflows',
            path: '/voice-workflows',
            icon: icon('solar:transmission-linear'),
            permission: 'voice.workflow.view',
          },
          {
            title: 'Phone Numbers',
            path: '/voice-phone-numbers',
            icon: icon('solar:phone-linear'),
            permission: 'voice.phone_number.view',
          },
          {
            title: 'Conversations',
            path: '/conversations',
            icon: icon('solar:chat-round-line-linear'),
            permission: 'voice.agent.view',
          },
          {
            title: 'Knowledge Base',
            path: '/knowledge-base',
            icon: icon('solar:book-2-linear'),
            permission: 'voice.agent.view',
          },
          {
            title: 'Voice Settings',
            path: '/voice-settings',
            icon: icon('solar:settings-linear'),
            permission: 'voice.settings.view',
          },
          {
            title: 'Voice Subscription',
            path: '/account/voice-subscription',
            icon: icon('solar:card-linear'),
            permission: 'voice.subscription.view',
          },
          {
            title: 'Voice Billing',
            path: '/account/voice-billing',
            icon: icon('solar:bill-list-linear'),
            permission: 'voice.billing.view',
          },
          // {
          //   title: 'Success Fees',
          //   path: '/account/voice-success-fees',
          //   icon: icon('solar:dollar-minimalistic-linear'),
          //   permission: 'campaign.view',
          // },
        ],
      },
    ],
  },
  /**
   * Measure (formerly ANALYZE)
   */
  {
    subheader: {
      title: 'Measure',
      family: true,
      familyColor: '#919EAB',
    },
    items: [
      { title: 'Dashboard', path: '/dashboard', icon: ICONS.dashboard, permission: 'report.view' },
      //this is not required for now
      // {
      //   title: 'Campaign Analytics',
      //   path: '/campaign-analytics',
      //   icon: ICONS.analytics,
      //   permission: 'report.view',
      // },
    ],
  },
  /**
   * Docs & Support (utility bottom item)
   */
  {
    subheader: {
      title: '__support__',
      family: false,
      hidden: true,
    },
    items: [
      {
        title: 'Docs & Support',
        path: 'https://docs.staging-api.boostt.ai/',
        icon: icon('solar:help-bold-duotone'),
      },
    ],
  },
];
